<template>
  <div class="pl-login">

    <!-- ===================== BRAND PANEL ===================== -->
    <div class="pl-brand pl-base-bg">
      <!-- Glows -->
      <div class="pl-glow pl-glow--a"></div>
      <div class="pl-glow pl-glow--b"></div>
      <div class="pl-glow pl-glow--c"></div>

      <!-- SVG grid -->
      <svg class="pl-grid" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid" width="52" height="52" patternUnits="userSpaceOnUse">
            <path d="M 52 0 L 0 0 0 52" fill="none" stroke="rgba(255,255,255,0.22)" stroke-width="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      <!-- Animated chart line at bottom -->
      <svg class="pl-chart" viewBox="0 0 600 400" preserveAspectRatio="none">
        <defs>
          <linearGradient id="plArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#bff5d6" stop-opacity="0.35" />
            <stop offset="1" stop-color="#bff5d6" stop-opacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0,330 L80,300 L160,318 L240,250 L320,268 L400,180 L480,150 L560,90 L600,70 L600,400 L0,400 Z"
          fill="url(#plArea)" />
        <path class="pl-chart-line"
          d="M0,330 L80,300 L160,318 L240,250 L320,268 L400,180 L480,150 L560,90 L600,70"
          fill="none" stroke="rgba(215,251,230,0.85)" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round" />
        <circle cx="400" cy="180" r="5" fill="#fff" opacity="0.9"/>
        <circle cx="560" cy="90"  r="5.5" fill="#fff" opacity="0.9"/>
      </svg>

      <!-- Brand content -->
      <div class="pl-brand-content">
        <!-- Icon clipped to square app icon only -->
        <div class="pl-icon-tile">
          <div class="pl-icon-clip">
            <img src="@/assets/ProfitLedgex.png" alt="ProfitLedgex" class="pl-icon-img" />
          </div>
        </div>
        <h1 class="pl-wordmark">ProfitLedgex</h1>
        <p class="pl-tagline">Intelligent business insights with seamless invoicing.</p>
        <div class="pl-accent-bar"></div>
      </div>
    </div>

    <!-- ===================== FORM PANEL ===================== -->
    <div class="pl-form-panel">
      <!-- Subtle top-right glow on form side -->
      <div class="pl-form-glow"></div>
      <div class="pl-form-glow-2"></div>

      <div class="pl-form">
        <p class="pl-eyebrow">Welcome back</p>
        <h2 class="pl-heading">Sign in to your account</h2>
        <p class="pl-sub">Pick up right where you left off.</p>

        <v-form ref="loginForm" v-model="isFormValid" lazy-validation @submit.prevent="handleLogin">
          <label class="pl-label">Email or Username</label>
          <v-text-field
            v-model.trim="email"
            type="text"
            placeholder="you@company.com or username"
            autocomplete="username"
            outlined
            hide-details="auto"
            :rules="emailRules"
            class="pl-field"
            color="#0a6cc0" />

          <label class="pl-label">Password</label>
          <v-text-field
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="Enter your password"
            autocomplete="current-password"
            outlined
            hide-details="auto"
            :rules="passwordRules"
            class="pl-field"
            color="#0a6cc0">
            <template #append>
              <v-icon
                tabindex="0"
                role="button"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                style="cursor: pointer;"
                @click="showPassword = !showPassword"
                @keydown.enter="showPassword = !showPassword">
                {{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </template>
          </v-text-field>

          <v-alert v-if="errorMessage" type="error" dense outlined class="mt-3 mb-0">
            {{ errorMessage }}
          </v-alert>

          <v-btn
            v-if="renewalToken"
            block
            depressed
            height="48"
            class="mt-3 pl-submit"
            @click="$router.push(`/renew?token=${renewalToken}`)">
            Renew Payment
          </v-btn>

          <v-btn
            type="submit"
            block
            depressed
            height="54"
            :loading="loading"
            :disabled="!isFormValid || loading"
            class="pl-submit mt-5">
            Sign in
          </v-btn>
        </v-form>

        <div class="pl-secure">
          <v-icon size="14" color="#a0adb8">mdi-lock-outline</v-icon>
          Secured with 256-bit SSL encryption
        </div>

        <p class="pl-switch">
          Don't have an account?
          <router-link to="/signup" class="pl-switch-link">Create one</router-link>
        </p>

        <v-btn
          v-if="showInstallBtn"
          block outlined large
          class="mt-4 pl-install"
          @click="installApp">
          <v-icon left size="18">mdi-cellphone-arrow-down</v-icon>
          Download App
        </v-btn>

        <v-alert
          v-if="showIosHint"
          type="info"
          dense
          outlined
          class="mt-4 pl-ios-hint">
          To install: tap <v-icon size="16">mdi-export-variant</v-icon> Share, then
          "Add to Home Screen".
        </v-alert>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginPage',

  mounted() {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showInstallBtn = true;
      this.showIosHint = false;
    });

    window.addEventListener('appinstalled', () => {
      this.showInstallBtn = false;
      this.showIosHint = false;
    });

    if (this.isStandalone()) return; // already running as an installed app

    if (this.isIos()) {
      // iOS Safari has no beforeinstallprompt API — only a manual
      // "Add to Home Screen" flow via the Share sheet.
      this.showIosHint = true;
    }
  },

  data: () => ({
    email: '',
    password: '',
    showPassword: false,
    isFormValid: false,
    loading: false,
    errorMessage: '',
    renewalToken: null,
    deferredPrompt: null,
    showInstallBtn: false,
    showIosHint: false,

    emailRules: [
      (v) => !!v || 'Email or username is required',
    ],
    passwordRules: [
      (v) => !!v || 'Password is required',
      (v) => v.length >= 6 || 'Password must be at least 6 characters',
    ],
  }),

  methods: {
    async handleLogin() {
      this.errorMessage = '';
      this.renewalToken = null;
      if (!this.$refs.loginForm.validate()) return;
      this.loading = true;
      try {
        await this.$store.dispatch('auth/login', {
          email: this.email.toLowerCase(),
          password: this.password,
        });
        this.$router.push('/dashboard');
      } catch (error) {
        const data = error.response?.data;
        this.errorMessage = data?.message || 'Invalid email or password';
        this.renewalToken = data?.code === 'PAYMENT_EXPIRED' ? data.renewalToken : null;
      } finally {
        this.loading = false;
      }
    },

    async installApp() {
      if (!this.deferredPrompt) return;
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      if (outcome !== 'accepted') console.log('User cancelled install');
      this.deferredPrompt = null;
      this.showInstallBtn = false;
    },

    isIos() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    },

    isStandalone() {
      return (
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true
      );
    },
  },
};
</script>

<style scoped>
/* ── Layout ── */
.pl-login {
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  font-family: 'Manrope', system-ui, sans-serif;
  color: #0e2433;
}


/* ════════════════════════════
   LEFT — Brand Panel
════════════════════════════ */
.pl-brand {
  position: relative;
  flex: 0 0 54%;
  overflow: hidden;
}

/* Glows */
.pl-glow { position: absolute; border-radius: 50%; pointer-events: none; }

.pl-glow--a {
  top: -90px; left: -90px;
  width: 440px; height: 440px;
  background: radial-gradient(circle,
    rgba(190,235,255,0.95) 0%,
    rgba(110,195,255,0.60) 32%,
    rgba(60,140,255,0.18) 58%,
    transparent 72%);
  filter: blur(38px);
  animation: pl-floatA 18s ease-in-out infinite;
}
.pl-glow--b {
  bottom: -160px; right: -80px;
  width: 480px; height: 480px;
  background: radial-gradient(circle, rgba(50,220,130,0.28), transparent 68%);
  filter: blur(90px);
  animation: pl-floatB 22s ease-in-out infinite;
}
.pl-glow--c {
  top: 38%; right: 12%;
  width: 220px; height: 220px;
  background: radial-gradient(circle, rgba(255,255,255,0.12), transparent 70%);
  filter: blur(80px);
  animation: pl-floatC 14s ease-in-out infinite;
}

/* SVG grid */
.pl-grid {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  z-index: 1; pointer-events: none;
}

/* Animated chart */
.pl-chart {
  position: absolute; left: 0; bottom: 0;
  width: 100%; height: 58%;
  opacity: 0.55; z-index: 2;
}
.pl-chart-line {
  stroke-dasharray: 1400;
  animation: pl-draw 2.8s cubic-bezier(.4,0,.2,1) forwards;
}

/* Brand block */
.pl-brand-content {
  position: absolute; inset: 0; z-index: 3;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 48px; text-align: center;
  animation: pl-rise 0.8s ease-out both;
}

/* Frosted glass icon tile */
.pl-icon-tile {
  padding: 16px;
  border-radius: 36px;
  background: rgba(255,255,255,0.12);
  box-shadow:
    0 28px 64px rgba(2,20,40,0.50),
    inset 0 0 0 1.5px rgba(255,255,255,0.22),
    inset 0 2px 4px rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* Clip wrapper — shows only the icon square, hides the brand-text below it */
.pl-icon-clip {
  width: 128px;
  height: 128px;
  overflow: hidden;
  border-radius: 26px;
  box-shadow: 0 8px 24px rgba(2,20,40,0.40);
  position: relative;
}
.pl-icon-img {
  display: block;
  position: absolute;
  top: 0; left: 0;
  width: 128px;
  height: auto;
}

.pl-wordmark {
  margin: 28px 0 0;
  font-weight: 800;
  font-size: 46px;
  letter-spacing: -0.025em;
  color: #fff;
  text-shadow:
    0 2px 12px rgba(2,20,40,0.35),
    0 0 60px rgba(120,210,255,0.25);
}

.pl-tagline {
  margin: 12px 0 0;
  font-size: 16px;
  font-weight: 500;
  color: rgba(225,245,255,0.88);
  letter-spacing: 0.01em;
}

.pl-accent-bar {
  margin-top: 28px;
  height: 3px; width: 56px;
  border-radius: 3px;
  background: linear-gradient(90deg, #7fd4ff, #74f0a8);
  box-shadow: 0 0 12px rgba(127,212,255,0.6);
}

/* ════════════════════════════
   RIGHT — Form Panel
════════════════════════════ */
.pl-form-panel {
  position: relative;
  flex: 1 1 46%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 48px;
  background: #f4f6f9;
  overflow: hidden;
}

/* Decorative glows on right side */
.pl-form-glow {
  position: absolute; top: -60px; right: -60px;
  width: 340px; height: 340px;
  background: radial-gradient(circle at 60% 40%, rgba(15,138,106,0.09), transparent 68%);
  pointer-events: none;
}
.pl-form-glow-2 {
  position: absolute; bottom: -80px; left: -40px;
  width: 280px; height: 280px;
  background: radial-gradient(circle, rgba(10,108,192,0.07), transparent 68%);
  pointer-events: none;
}

.pl-form {
  position: relative;
  width: 100%;
  max-width: 400px;
  animation: pl-rise 0.8s ease-out 0.08s both;
}

.pl-eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #0a8a6a;
}

.pl-heading {
  margin: 10px 0 6px;
  font-weight: 800;
  font-size: 30px;
  letter-spacing: -0.025em;
  color: #0b1e2d;
  line-height: 1.2;
}

.pl-sub {
  margin: 0 0 26px;
  font-size: 14.5px;
  color: #64747f;
  font-weight: 400;
}

.pl-label {
  display: block;
  margin: 16px 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374955;
  letter-spacing: 0.01em;
}

/* Input fields */
.pl-field >>> .v-input__slot {
  border-radius: 12px !important;
  background: #ffffff !important;
  min-height: 50px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important;
}
.pl-field >>> fieldset {
  border-color: #cdd5dc !important;
  border-width: 1.5px !important;
}
.pl-field >>> .v-input--is-focused fieldset {
  border-color: #0a6cc0 !important;
  border-width: 2px !important;
}
.pl-field >>> input {
  font-size: 14.5px;
  color: #0b1e2d;
}
.pl-field >>> input::placeholder { color: #a8b5bf; }

/* Submit button */
.pl-submit {
  border-radius: 12px !important;
  background: linear-gradient(135deg, #0a5fb5 0%, #0d8f75 52%, #16b358 100%) !important;
  color: #fff !important;
  font-weight: 700 !important;
  letter-spacing: 0.02em !important;
  font-size: 16px !important;
  text-transform: none !important;
  box-shadow:
    0 8px 24px rgba(10,108,192,0.30),
    0 4px 8px rgba(13,143,117,0.20) !important;
  transition: opacity 0.2s, box-shadow 0.2s !important;
}
.pl-submit:hover {
  box-shadow:
    0 12px 32px rgba(10,108,192,0.38),
    0 6px 12px rgba(13,143,117,0.28) !important;
}

/* SSL note */
.pl-secure {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 22px;
  color: #9daab5;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.pl-switch {
  margin-top: 18px;
  text-align: center;
  font-size: 13.5px;
  color: #64747f;
}

.pl-switch-link {
  color: #0a6cc0;
  font-weight: 700;
  text-decoration: none;
}
.pl-switch-link:hover {
  text-decoration: underline;
}

.pl-install {
  position: relative;
  overflow: hidden;
  border: 1.5px solid rgba(10,108,192,0.18) !important;
  color: #0b1e2d !important;
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0.01em !important;
  background: linear-gradient(180deg, #ffffff 0%, #f3f7fb 60%, #e9f1f8 100%) !important;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.9) inset,
    0 -1px 0 rgba(10,108,192,0.08) inset,
    0 6px 16px rgba(10,60,110,0.10) !important;
  transition: box-shadow 0.2s, transform 0.2s !important;
}
.pl-install::before {
  content: "";
  position: absolute;
  top: 0; left: -60%;
  width: 45%; height: 100%;
  background: linear-gradient(120deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.75) 50%,
    rgba(255,255,255,0) 100%);
  transform: skewX(-20deg);
  pointer-events: none;
}
.pl-install:hover {
  box-shadow:
    0 1px 0 rgba(255,255,255,0.9) inset,
    0 -1px 0 rgba(10,108,192,0.08) inset,
    0 10px 22px rgba(10,60,110,0.16) !important;
  transform: translateY(-1px);
}
.pl-install >>> .v-icon {
  color: #0a6cc0 !important;
}

.pl-ios-hint {
  font-size: 12.5px;
  border-radius: 12px !important;
}
.pl-ios-hint >>> .v-icon {
  vertical-align: text-bottom;
}

/* ── Animations ── */
@keyframes pl-floatA {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(30px, -42px); }
}
@keyframes pl-floatB {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(-28px, 36px); }
}
@keyframes pl-floatC {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(18px, 26px); }
}
@keyframes pl-draw {
  from { stroke-dashoffset: 1400; }
  to   { stroke-dashoffset: 0; }
}
@keyframes pl-rise {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Mobile ── */
@media (max-width: 860px) {
  /* Root: stack vertically, allow full scroll */
  .pl-login {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
  }

  /* Brand panel: compact hero */
  .pl-brand {
    flex: 0 0 auto;
    height: 270px;
    min-height: unset;
  }

  .pl-brand-content {
    padding: 24px 20px;
  }

  .pl-icon-tile {
    padding: 10px;
    border-radius: 22px;
  }

  .pl-icon-clip {
    width: 76px;
    height: 76px;
    border-radius: 16px;
  }

  /* Same uncropped fill as desktop (top:0, left:0, width = clip size) —
     the old values here cropped into the image instead of scaling it,
     exposing a sliver of the source PNG's background at the edges. */
  .pl-icon-img {
    width: 76px;
    height: auto;
    top: 0;
    left: 0;
  }

  .pl-wordmark {
    font-size: 26px;
    margin: 12px 0 0;
  }

  .pl-tagline {
    font-size: 13px;
    margin: 6px 0 0;
    white-space: normal;
  }

  .pl-accent-bar {
    margin-top: 12px;
    width: 36px;
  }

  .pl-glow--a {
    width: 240px; height: 240px;
    top: -50px; left: -50px;
    filter: blur(28px);
  }

  /* Form panel: no centering, starts from top, natural scroll */
  .pl-form-panel {
    flex: 1;
    min-height: unset;
    align-items: flex-start;
    padding: 36px 24px 52px;
  }

  .pl-form { max-width: 100%; }

  .pl-heading { font-size: 24px; }

  .pl-sub {
    font-size: 14px;
    margin-bottom: 18px;
  }
}
</style>
